import { ChangeEvent, EventHandler, useCallback, KeyboardEvent, useState, useMemo, useEffect } from 'react'
import { useDebounceValue } from 'usehooks-ts'

import { useServices } from '@percent/admin-dashboard/containers/service/ServiceContext'
import { useFilterValue } from '@percent/admin-dashboard/common/hooks'
import { useQuery } from '@percent/utility'
import { Organisation } from '@percent/admin-dashboard/api/actions/admin/admin.types'

export const useOrganisationFilter = ({
  filterValues,
  onFilterValuesChange,
  onClearFilterValues
}: {
  filterValues: string[]
  onFilterValuesChange: (filterValues: string[]) => void
  onClearFilterValues?: () => void
}) => {
  const [organisationSearch, setOrganisationSearch] = useState<string>('')
  const [selectedOrganisationOptions, setSelectedOrganisationOptions] = useState<
    Array<{ label: string; value: string }>
  >([])
  const filterValuesKey = (filterValues ?? []).length ? (filterValues ?? []).join(',') : ''

  const [selectedOrganisations, setSelectedOrganisations] = useFilterValue<string[]>(
    filterValues ?? [],
    [filterValuesKey],
    () => {
      if (!filterValues?.length) {
        setOrganisationSearch('')
        setSelectedOrganisationOptions([])
      }
    }
  )

  const [debouncedSearch] = useDebounceValue(organisationSearch, 500)
  const { adminService } = useServices()

  const initialOrganisationIds = filterValues ?? []
  const shouldFetchByIds = initialOrganisationIds.length > 0 && selectedOrganisationOptions.length === 0
  const shouldFetchBySearch = !!debouncedSearch
  const isAtLeast3Characters = organisationSearch.length >= 3

  const { data, isLoading } = useQuery<
    { query?: string; organisationIds?: string[]; onlyActive?: boolean },
    Organisation[]
  >(
    ['v1/admin/organisations', shouldFetchByIds ? initialOrganisationIds : debouncedSearch],
    adminService.getOrganisations,
    shouldFetchByIds
      ? { organisationIds: initialOrganisationIds, onlyActive: false }
      : { query: debouncedSearch || undefined, onlyActive: false },
    {
      enabled: shouldFetchByIds || (shouldFetchBySearch && isAtLeast3Characters)
    }
  )

  useEffect(() => {
    if (shouldFetchByIds && data && data.length > 0) {
      const options = data.map(org => ({ label: org.name, value: org.id }))
      setSelectedOrganisationOptions(options)
    }
  }, [data, shouldFetchByIds])

  const organisationOptions = useMemo(() => {
    const searchResults = data?.map(org => ({ label: org.name, value: org.id })) || []
    const filteredSearchResults = searchResults.filter(
      option => !selectedOrganisationOptions.some(selected => selected.value === option.value)
    )

    return [...selectedOrganisationOptions, ...filteredSearchResults]
  }, [data, selectedOrganisationOptions])

  const handleSearchChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    setOrganisationSearch(event.target.value)
  }, [])

  const handleClearSearchValue = useCallback(() => {
    setOrganisationSearch('')
  }, [])

  const handleKeyPress: EventHandler<KeyboardEvent<HTMLInputElement>> = useCallback(event => {
    if (event.key === 'Enter') {
      setOrganisationSearch((event.target as HTMLInputElement).value)
    }

    if (event.key === 'Escape') {
      setOrganisationSearch('')
    }
  }, [])

  const onOrganisationChange = useCallback(
    (organisations: string[]) => {
      setSelectedOrganisations(organisations)

      const newSelectedOptions = organisationOptions.filter(option => organisations.includes(option.value))
      setSelectedOrganisationOptions(newSelectedOptions)

      if (organisations.length === 0) {
        onClearFilterValues?.()
      }
    },
    [setSelectedOrganisations, organisationOptions, onClearFilterValues]
  )

  const onOrganisationsSearchButtonClick = useCallback(() => {
    onFilterValuesChange(selectedOrganisations)
  }, [selectedOrganisations, onFilterValuesChange])

  return {
    selectedOrganisations,
    organisationOptions,
    onOrganisationChange,
    isOrganisationFilterLoading: isLoading,
    selectedOrganisationOptions,
    organisationSearch: {
      onOrganisationSearchChange: handleSearchChange,
      onClearOrganisationSearchValue: handleClearSearchValue,
      organisationSearchValue: organisationSearch,
      onOrganisationsSearchButtonClick,
      handleKeyPress
    }
  }
}
